import { useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import { useStorageGroups, useStorageUpload } from '../hooks';
import { GroupSection } from '../components';
import type { StorageArcheConfig } from '../types';
import { createStorageQueryKeys } from '../types';

interface StorageBrowsePageProps {
  config: StorageArcheConfig;
}

export function StorageBrowsePage({ config }: StorageBrowsePageProps) {
  const { user } = useAuth();
  const hasRole = useMemo(
    () => user?.roles?.includes(config.requiredRole) ?? false,
    [user?.roles, config.requiredRole],
  );

  const keys = useMemo(
    () => createStorageQueryKeys(config.queryKeyNamespace),
    [config.queryKeyNamespace],
  );

  const { data, isLoading, error } = useStorageGroups(config.api, keys.groups());
  const queryClient = useQueryClient();
  const upload = useStorageUpload(config.api, keys.all);

  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [pendingGroups, setPendingGroups] = useState<string[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [...keys.all] });
  }, [queryClient, keys.all]);

  const handleDelete = useCallback(
    async (key: string) => {
      if (!window.confirm(`Delete ${key}?`)) return;
      setDeleteError(null);
      try {
        await config.api.deleteFile(key);
        // Invalidate the specific prefix that contained this file
        const filePrefix = key.substring(0, key.lastIndexOf('/') + 1);
        queryClient.invalidateQueries({ queryKey: [...keys.files(filePrefix)] });
        // Also invalidate parent group listing (folder counts may change)
        const parentPrefix = filePrefix.replace(/[^/]+\/$/, '');
        if (parentPrefix !== filePrefix) {
          queryClient.invalidateQueries({ queryKey: [...keys.files(parentPrefix)] });
        }
      } catch (err) {
        setDeleteError(err instanceof Error ? err.message : 'Failed to delete file');
      }
    },
    [queryClient, keys, config.api],
  );

  const handleCreateGroup = useCallback(async () => {
    const name = newGroupName.trim();
    if (!name || !/^[a-zA-Z0-9_-]+$/.test(name)) return;

    // Create a .keep placeholder so the group persists in S3
    try {
      const { uploadUrl } = await config.api.getUploadUrl(
        `${name}/.keep`,
        'application/octet-stream',
        1,
      );
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: new Uint8Array(1),
      });
      invalidateAll();
    } catch {
      // Fall back to showing the group locally until a file is uploaded
      setPendingGroups((prev) => [...prev, name]);
    }

    setNewGroupName('');
    setShowNewGroup(false);
  }, [newGroupName, invalidateAll, config.api]);

  const handleUploadToGroup = useCallback(
    async (groupPrefix: string, files: FileList | null) => {
      if (!files || files.length === 0) return;
      for (const file of Array.from(files)) {
        const key = `${groupPrefix}${file.name}`;
        await upload.mutateAsync({ file, key });
      }
      // Remove from pending if it was a pending group
      const groupName = groupPrefix.replace(/\/$/, '');
      setPendingGroups((prev) => prev.filter((g) => g !== groupName));
      invalidateAll();
    },
    [upload, invalidateAll],
  );

  if (!hasRole) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <ShieldAlert className="h-12 w-12 mb-3" />
        <p className="text-lg font-medium">Access required</p>
        <p className="text-sm">{config.accessDeniedMessage}</p>
      </div>
    );
  }

  // Merge server groups with locally-pending groups (not yet in S3)
  const serverGroupNames = new Set(data?.groups.map((g) => g.name) ?? []);
  const visiblePendingGroups = pendingGroups.filter((g) => !serverGroupNames.has(g));

  return (
    <div className="flex flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{config.title}</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowNewGroup(!showNewGroup)}>
              <Plus className="h-4 w-4 mr-1" />
              New Group
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showNewGroup && (
            <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/20">
              <Input
                placeholder="Group name (e.g. dfe-items)"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                className="max-w-xs"
              />
              <Button
                size="sm"
                disabled={!newGroupName.trim() || !/^[a-zA-Z0-9_-]+$/.test(newGroupName.trim())}
                onClick={handleCreateGroup}
              >
                Create
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowNewGroup(false)}>
                Cancel
              </Button>
            </div>
          )}

          {deleteError && <p className="text-destructive text-sm">{deleteError}</p>}

          {isLoading && <p className="text-muted-foreground">Loading...</p>}
          {error && (
            <p className="text-destructive">
              {error instanceof Error ? error.message : 'Failed to load files'}
            </p>
          )}

          {visiblePendingGroups.map((name) => (
            <GroupWithUpload
              key={name}
              name={name}
              prefix={`${name}/`}
              cdnBaseUrl={data?.cdnBaseUrl ?? undefined}
              onDelete={handleDelete}
              onUpload={handleUploadToGroup}
              api={config.api}
              queryKeyAll={keys.all}
              queryKeyFiles={keys.files}
              acceptedFileTypes={config.acceptedFileTypes}
            />
          ))}

          {data?.groups.map((group) => (
            <GroupWithUpload
              key={group.prefix}
              name={group.name}
              prefix={group.prefix}
              cdnBaseUrl={data.cdnBaseUrl ?? undefined}
              onDelete={handleDelete}
              onUpload={handleUploadToGroup}
              api={config.api}
              queryKeyAll={keys.all}
              queryKeyFiles={keys.files}
              acceptedFileTypes={config.acceptedFileTypes}
            />
          ))}

          {!isLoading && data?.groups.length === 0 && visiblePendingGroups.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              No groups yet. Click "New Group" to create one.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GroupWithUpload({
  name,
  prefix,
  cdnBaseUrl,
  onDelete,
  onUpload,
  api,
  queryKeyAll,
  queryKeyFiles,
  acceptedFileTypes,
}: {
  name: string;
  prefix: string;
  cdnBaseUrl?: string;
  onDelete: (key: string) => void;
  onUpload: (prefix: string, files: FileList | null) => Promise<void>;
  api: StorageBrowsePageProps['config']['api'];
  queryKeyAll: readonly string[];
  queryKeyFiles: (prefix: string) => readonly string[];
  acceptedFileTypes?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-1">
      <GroupSection
        name={name}
        prefix={prefix}
        cdnBaseUrl={cdnBaseUrl}
        onDelete={onDelete}
        onUpload={onUpload}
        api={api}
        queryKeyAll={queryKeyAll}
        queryKeyFiles={queryKeyFiles}
        acceptedFileTypes={acceptedFileTypes}
      />
      <div className="flex justify-end px-1">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFileTypes}
          className="hidden"
          onChange={(e) => {
            onUpload(prefix, e.target.files);
            if (e.target) e.target.value = '';
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          onClick={() => fileInputRef.current?.click()}
        >
          <Plus className="h-3 w-3 mr-1" />
          Upload to {name}
        </Button>
      </div>
    </div>
  );
}
